import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';

import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/styles.css';
import '../styles/formularios.css';
import logoInstituto from '../components/images/logo.png';
import logoSEP from '../components/images/SEP_Logo_2019.png';
import { useLocation, useNavigate } from 'react-router-dom';
import { resolveApiBaseList } from '../utils/apiBase';


import AOS from 'aos';
import 'aos/dist/aos.css';

// Si prefieres importar imágenes por JS, descomenta y usa src={logoInstituto} / src={logoSEP}
// import logoInstituto from '../components/images/logo.png';
// import logoSEP from '../components/images/SEP_Logo_2019.svg.png';

const RegistrarMateria = () => {
  // Campos
  const [idMateria, setIdMateria] = useState('Auto');
  const [idCategoriaMateria, setIdCategoriaMateria] = useState('');
  const [nombreMateria, setNombreMateria] = useState('');
  const [gradoAsignado, setGradoAsignado] = useState('');
  const [status] = useState('A'); // Activo (deshabilitado como en tu HTML)

  // Datos
  const [categorias, setCategorias] = useState([]); // [{IdCategoriaMateria, Descripcion}]
  const [loading, setLoading] = useState(false);

  const apiBases = useMemo(() => resolveApiBaseList(), []);

  const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
    let lastError = null;
    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;
      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // keep raw message
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`⚠️ Falló la petición a ${url}:`, error);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        const trimmed = text.trim();
        if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
          return trimmed ? JSON.parse(trimmed) : {};
        }

        lastError = new Error(`Respuesta inválida desde ${url}`);
        console.warn(`⚠️ Contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  const sendJsonWithFallback = useCallback(async (path, payload, method = 'POST') => {
    let lastError = null;
    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;
      try {
        const response = await fetch(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // keep raw message
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`⚠️ Falló la solicitud ${method} a ${url}:`, error);
          continue;
        }

        const trimmed = text.trim();
        if (trimmed && trimmed.startsWith('<')) {
          const error = new Error(`Respuesta inválida desde ${url}`);
          lastError = error;
          console.warn(`⚠️ Contenido no válido desde ${url}`);
          continue;
        }

        if (!trimmed) return { text: '', parsed: null, url };

        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
          try {
            const parsed = JSON.parse(trimmed);
            return { text: parsed?.mensaje || parsed?.message || trimmed, parsed, url };
          } catch (parseErr) {
            console.warn(`⚠️ No se pudo parsear respuesta JSON desde ${url}:`, parseErr);
          }
        }

        return { text: trimmed, parsed: null, url };
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Error de red al enviar ${method} a ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  const navigate = useNavigate();
    const location = useLocation();
    const handleSalir = () => {
      const rol = resolveRolBase(location.pathname);
      if (rol) {
        navigate(`/${rol}/inicio`, { replace: true });
      } else {
        // Fallback por si no se detecta rol ni en path ni en localStorage:
        navigate('/inicio', { replace: true });
      }
    };

  // === equivalente a obtenerSiguienteIdMateria() de tu JS ===
  const obtenerSiguienteIdMateria = useCallback(async () => {
    try {
      const data = await fetchJsonWithFallback('/materias/siguiente-id');
      // Tu JS esperaba: if (data.length > 0) data[0].SiguienteId
      const siguiente = Array.isArray(data) && data.length > 0
        ? data[0].SiguienteId
        : (data?.siguienteId ?? 'Auto');
      setIdMateria(siguiente ?? 'Auto');
    } catch (err) {
      console.error('Error al obtener ID:', err);
      setIdMateria('Auto');
      await Swal.fire('Error', err.message || 'No se pudo obtener el ID de la materia.', 'error');
    }
  }, [fetchJsonWithFallback]);

  // === equivalente a cargarCategoriasMateria() de tu JS ===
  const cargarCategoriasMateria = useCallback(async () => {
    try {
      const data = await fetchJsonWithFallback('/categorias/obtener-descripciones');
      const arreglo = Array.isArray(data) ? data : [];
      setCategorias(arreglo);
    } catch (err) {
      console.error('Error al cargar categorías:', err);
      await Swal.fire('Error', err.message || 'No se pudieron cargar las categorías.', 'error');
    }
  }, [fetchJsonWithFallback]);

  useEffect(() => {
    AOS.init();
    obtenerSiguienteIdMateria();
    cargarCategoriasMateria();
  }, [cargarCategoriasMateria, obtenerSiguienteIdMateria]);

  // === equivalente a registrarMateria() de tu JS ===
  const registrarMateria = async (e) => {
    e.preventDefault();

    if (!idCategoriaMateria || !nombreMateria.trim() || !gradoAsignado || !status) {
      await Swal.fire('Campos vacíos', 'Todos los campos son obligatorios.', 'warning');
      return;
    }

    setLoading(true);
    try {
      const { parsed, text } = await sendJsonWithFallback('/materias/registrar', {
        IdCategoriaMateria: parseInt(idCategoriaMateria, 10),
        Nombre: nombreMateria.trim(),
        GradoAsignado: parseInt(gradoAsignado, 10),
        Status: status
      });

      if (parsed?.error) {
        throw new Error(parsed.error);
      }

      await Swal.fire('Éxito', text || 'Materia registrada correctamente.', 'success');

      // Reset como tu JS + pedir nuevo ID
      setIdCategoriaMateria('');
      setNombreMateria('');
      setGradoAsignado('');
      await obtenerSiguienteIdMateria();
    } catch (err) {
      console.error('Error:', err);
      await Swal.fire('Error', err.message || 'Error al registrar la materia.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      {/* Encabezado (mismo layout que tu HTML) */}
      <div className="d-flex justify-content-between align-items-center mb-4">
          <img src={logoInstituto} alt="Logo Instituto" className="logo" />
            <div className="flex-grow-1 text-center">
               <h4 className="mb-0">Instituto Silviano Carrillo</h4>
                <small className="text-muted fs-5">Registrar Materia</small>
              </div>
            <img src={logoSEP} alt="Logo SEP" className="logo" />
      </div>

      {/* Formulario Materia */}
      <div className="mb-4 p-4 rounded bg-light shadow-sm">
        <h6 className="mb-3">Registro de Materia</h6>

        <form className="row g-3" id="formMateria" onSubmit={registrarMateria}>
          {/* ID Materia deshabilitado */}
          <div className="col-md-2">
            <label htmlFor="idMateria" className="form-label">ID Materia</label>
            <input
              type="text"
              className="form-control"
              id="idMateria"
              placeholder="Auto"
              value={idMateria}
              disabled
            />
          </div>

          {/* Categoría de Materia */}
          <div className="col-md-4">
            <label htmlFor="idCategoriaMateria" className="form-label">Categoría de Materia</label>
            <select
              className="form-select"
              id="idCategoriaMateria"
              value={idCategoriaMateria}
              onChange={(e) => setIdCategoriaMateria(e.target.value)}
            >
              <option value="" disabled>Selecciona una categoría</option>
              {categorias.map((cat) => (
                <option key={cat.IdCategoriaMateria} value={cat.IdCategoriaMateria}>
                  {cat.Descripcion}
                </option>
              ))}
            </select>
          </div>

          {/* Nombre de la Materia */}
          <div className="col-md-4">
            <label htmlFor="nombreMateria" className="form-label">Nombre de la Materia</label>
            <input
              type="text"
              className="form-control"
              id="nombreMateria"
              placeholder="Nombre de la materia..."
              value={nombreMateria}
              onChange={(e) => setNombreMateria(e.target.value)}
            />
          </div>

          {/* Grado Asignado */}
          <div className="col-md-4">
            <label htmlFor="grado" className="form-label">Grado Asignado</label>
            <select
              className="form-select"
              id="grado"
              value={gradoAsignado}
              onChange={(e) => setGradoAsignado(e.target.value)}
            >
              <option value="" disabled>Selecciona un grado</option>
              <option value="1">1°</option>
              <option value="2">2°</option>
              <option value="3">3°</option>
              <option value="4">4°</option>
              <option value="5">5°</option>
              <option value="6">6°</option>
            </select>
          </div>

          {/* Status (deshabilitado) */}
          <div className="col-md-2">
            <label htmlFor="status" className="form-label">Status</label>
            <select className="form-select" id="status" value={status} disabled>
              <option value="A">Activo</option>
              <option value="I">Inactivo</option>
            </select>
          </div>
        </form>
      </div>

      {/* Botones fuera del form */}
      <div className="d-flex justify-content-center gap-4">
        <button
          id="btnRegistrarMateria"
          className="btn btn-success px-4"
          onClick={registrarMateria}
          disabled={loading}
        >
          {loading ? 'Guardando...' : 'Registrar'}
        </button>
        <button type="button" className="btn btn-danger px-4" onClick={handleSalir}>
             Salir
        </button>
      </div>
    </div>
  );
};

/* ----------------- Helper para detectar el rol ----------------- */
function resolveRolBase(pathname) {
  // 1) Intenta desde la URL actual
  const roles = ['directora', 'coordinadora', 'docente', 'alumno'];
  const seg = pathname.split('/').filter(Boolean)[0];
  if (roles.includes(seg)) return seg;

  // 2) Respaldo desde localStorage.role (números mapeados a slugs)
  const roleId = Number(localStorage.getItem('role'));
  const map = { 1: 'directora', 2: 'coordinadora', 3: 'docente', 4: 'alumno' };
  return map[roleId] || null;
}

export default RegistrarMateria;
